import { toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery, useQueryCache } from '@pinia/colada'
import { CACHE_KEYS, CACHE_TTL } from '@/services/cache'
import { ClaudeTeamApi } from './claude-team.api'
import type { AttentionAnswer, TeamDecisionsAnswer, TeamFeedAnswer, TeamList, TeamReadyAnswer } from './claude-team.types'

type FeedList = Exclude<TeamList, 'decisions'>

/**
 * The team lists, with caching.
 *
 * Architecture: View → claude-team.cached.ts → claude-team.api.ts
 */
export function useClaudeTeamService() {
  const queryCache = useQueryCache()

  const useDecisions = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    return useQuery({
      key: () => CACHE_KEYS.claudeTeam.list('decisions'),
      query: (): Promise<TeamDecisionsAnswer> => ClaudeTeamApi.getDecisions(),
      staleTime: CACHE_TTL.CLAUDE_TEAM,
      enabled: () => (options.enabled === undefined ? true : toValue(options.enabled))
    })
  }

  const useFeed = (list: FeedList) => {
    return useQuery({
      key: () => CACHE_KEYS.claudeTeam.list(list),
      query: async (): Promise<TeamFeedAnswer> => {
        const answer = await ClaudeTeamApi.getFeed(list)
        return { asOf: answer.asOf, days: answer.days ?? null, cards: answer[list] ?? [] }
      },
      staleTime: CACHE_TTL.CLAUDE_TEAM
    })
  }

  const useReady = () => {
    return useQuery({
      key: () => CACHE_KEYS.claudeTeam.list('ready'),
      query: (): Promise<TeamReadyAnswer> => ClaudeTeamApi.getReady(),
      staleTime: CACHE_TTL.CLAUDE_TEAM
    })
  }

  /** Needs attention's delivery checks; the side nav reads the same query for its badge. */
  const useAttention = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    return useQuery({
      key: () => CACHE_KEYS.claudeTeam.list('attention'),
      query: (): Promise<AttentionAnswer> => ClaudeTeamApi.getAttention(),
      staleTime: CACHE_TTL.CLAUDE_TEAM,
      enabled: () => (options.enabled === undefined ? true : toValue(options.enabled))
    })
  }

  /** Every list re-reads, since a press can move a ticket between them. */
  const refreshLists = () => queryCache.invalidateQueries({ key: CACHE_KEYS.claudeTeam.all() })

  return { useDecisions, useFeed, useReady, useAttention, refreshLists }
}
